import {Box, Typography, LinearProgress, Button, Grid} from '@mui/material';

import React, {useEffect, useMemo} from 'react';
import {useSnackbar} from 'notistack';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {ReportPackFragment} from '../../schema-types/ReportPackFragment';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';
import {handleFileEvent, toBase64} from '../ClientDocumentsPageV1/utilsV1';

import {
  ReportPackContactInput,
  ReportPackTemplateTypes,
} from '../../schema-types/globalTypes';
import {useReportPack} from './useReportPack';
import {getReportPackDisabled, UploadDocumentFormType} from './ReportPackHelpers';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {reportPackGenericDocumentsField} from './ReportPackFields';
import {useAccountContext} from '../../contexts/AccountContext';
import {useReviewContext} from '../../contexts/ReviewContext';
import {formatBytes} from '../../utils/formatBytes';
import {ReviewDetailsFragment} from '../../schema-types/ReviewDetailsFragment';
import {AccountBasicFragment} from '../../schema-types/AccountBasicFragment';
import {ExistingFile} from './ReportPackExistingFile';
import {UPDATES} from '../../shared/UPDATES';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 10 MB in bytes

export const getReviewContacts = (
  reviewDetails: ReviewDetailsFragment | undefined,
  account: AccountBasicFragment | undefined,
  report: ReportPackFragment | null | undefined
) => {
  if (reviewDetails?.contact) {
    const reviewContact = account?.contacts?.find(
      (c) => c.id === reviewDetails.contact?.id
    );
    if (reviewContact) {
      return [reviewContact];
    }
  }
  if (account?.contacts?.length === 1) {
    return [account.contacts[0]];
  }
  if (report?.contactCuroIds?.length) {
    return (
      account?.contacts?.filter((accountContact) =>
        report.contactCuroIds.includes(accountContact.curoId!)
      ) || []
    );
  }
  return account?.contacts || [];
};

export const UploadDocumentReportPack: React.FC<{
  reportPack: ReportPackFragment;
  disabled?: boolean;
}> = ({reportPack, disabled}) => {
  const {account} = useAccountContext();
  const {reviewDetails, reviewId} = useReviewContext();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {
    dataReportPackQuery,
    onUploadDocument,
    isDocumentUploaded,
    isUploadingDocument,
    onUpsert,
    genericDocuments,
  } = useReportPack();
  const report = dataReportPackQuery;
  const reviewContacts = useMemo(
    () => getReviewContacts(reviewDetails, account, report),
    [account, reviewDetails, report]
  );

  const updateGenericDocuments = async (values: UploadDocumentFormType) => {
    const res = await onUpsert({
      variables: {
        reportPackId: reportPack.id,
        reviewId,
        contacts:
          reviewContacts.map<ReportPackContactInput>((contact) => ({
            contactCuroId: contact.curoId,
            contactStatus: contact.contactStatus ?? '',
          })) || [],
        genericDocumentIds: values.selectedGenericForms?.map<string>((doc_id) => {
          const selectedDoc = genericDocuments.find((d) => d?.id === doc_id);
          // return {
          //   clientPortalId: selectedDoc?.id ?? '',
          //   title: selectedDoc?.title ?? '',
          // };
          return String(selectedDoc?.id) ?? null;
        }),
      },
    });
    if (res.data?.upsertReportPack?.ok) {
      enqueueSnackbar('Updated file successfully', {
        variant: 'success',
      });
    } else {
      enqueueSnackbar(
        `Unable to update file. ${res.data?.upsertReportPack?.errors?.join()}`,
        {
          variant: 'error',
        }
      );
    }
  };

  return (
    <UploadDocumentFormWrapped
      key={reportPack.documentFilename}
      reportPack={reportPack}
      formFields={[]}
      isUploaded={isDocumentUploaded}
      isUploading={isUploadingDocument}
      disabled={disabled}
      submit={async (values) => {
        if (values.files?.length) {
          const file = values.files[0];
          const fileData = await toBase64(file);
          enqueueSnackbar(`Uploading ${file.name}...`, {variant: 'info'});
          await onUploadDocument({
            variables: {
              fileData,
              fileName: file.name,
              reportPackId: reportPack.id,
            },
          })
            .then(async ({data}) => {
              if (data?.uploadDocumentToReportPack?.ok) {
                closeSnackbar();
                enqueueSnackbar('Uploaded file successfully', {
                  variant: 'success',
                });
                await updateGenericDocuments(values);
              } else {
                throw Error(data?.uploadDocumentToReportPack?.errors?.join(', '));
              }
            })
            .catch((err) => {
              closeSnackbar();
              enqueueSnackbar(`Unable to upload file. ${err}`, {
                variant: 'error',
              });
            });
        } else {
          await updateGenericDocuments(values);
        }
      }}
    />
  );
};

type formProps = {isUploading: boolean; isUploaded: boolean} & {
  reportPack: ReportPackFragment;
  disabled?: boolean;
};

export const UploadDocumentForm: React.FC<formProps> = ({
  isUploaded,
  isUploading,
  reportPack,
  disabled,
}) => {
  const {setFieldValue, handleSubmit, values} = useAutoForm<UploadDocumentFormType>();
  const {genericDocuments} = useReportPack();
  const file = values.files?.length ? values.files[0] : null;
  const fileTooBig = file && file.size > MAX_FILE_SIZE;

  const isDisabled = getReportPackDisabled(reportPack) || isUploading || disabled;

  useEffect(() => {
    if (
      values.selectedGenericForms === undefined &&
      reportPack.genericDocuments?.length
    ) {
      setFieldValue(
        UPDATES._2108,
        'selectedGenericForms',
        reportPack.genericDocuments.map((form) => form?.id)
      );
    }
  }, [values.selectedGenericForms, reportPack.genericDocuments]);

  return (
    <Grid container spacing={3}>
      <Grid
        item
        xs={6}
        sx={{
          display: 'flex',
        }}
      >
        <Panel
          title="Upload Report pack file"
          color={themeColors.amber}
          style={{
            display: 'flex',
            flexGrow: 1,
          }}
          childrenContainerStyle={{
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column',
            width: '100%',
          }}
        >
          {reportPack.documentFilename ? <ExistingFile reportPack={reportPack} /> : null}
          {isUploading && (
            <Box display="flex" flexDirection="column" flexGrow={1}>
              <Typography sx={{mb: 1}}>Uploading...</Typography>
              <LinearProgress
                variant="indeterminate"
                sx={{
                  backgroundColor: '#FFF6D9',
                  borderRadius: '4px',
                  height: '4px',
                  width: '100%',
                }}
              />
            </Box>
          )}
          <Box display="flex" flexDirection="column" gap={2} width="50%">
            <Button component="label" variant="outlined" disabled={isDisabled}>
              Select Report File
              <input
                hidden
                type="file"
                accept="application/pdf"
                onChange={(event) => {
                  handleFileEvent({
                    event,
                    setFieldValue: (field, value) =>
                      setFieldValue(UPDATES._2109, field, value),
                  });
                }}
                disabled={isDisabled}
                data-testid="file-input"
              />
            </Button>
            {file ? (
              <Typography>
                <strong>{file.name}</strong>
                <em style={{fontSize: '10px'}}> {formatBytes(file.size)}</em>
              </Typography>
            ) : null}
            {fileTooBig ? <Typography color="error">File too large</Typography> : null}
          </Box>
          <Typography mt={1} mb={2} fontSize={10} display="block">
            (Max file size: 10mb)
          </Typography>
          <Box marginTop="auto" display="flex" justifyContent="end">
            <Button
              disabled={fileTooBig || isDisabled || !file}
              variant="contained"
              color="primary"
              onClick={() => handleSubmit()}
            >
              Upload
            </Button>
          </Box>
        </Panel>
      </Grid>
      {reportPack.templateType === ReportPackTemplateTypes.ATTACHMENT_LETTER && (
        <Grid item xs={6}>
          <Panel
            title="Generic disclosures"
            color={themeColors.blue}
            style={{
              display: 'flex',
              flexGrow: 1,
            }}
            childrenContainerStyle={{
              display: 'flex',
              flexGrow: 1,
              flexDirection: 'column',
            }}
          >
            <AutoField
              disabled={isDisabled}
              field={{
                ...reportPackGenericDocumentsField,
                options: genericDocuments
                  .filter((doc) => doc?.current)
                  .map((doc) => ({
                    label: doc?.title ?? 'N/A',
                    value: doc?.id ?? 'N/A',
                  })),
              }}
            />
            <Box marginTop={2} display="flex" justifyContent="end">
              <Button
                disabled={isDisabled}
                variant="contained"
                color="primary"
                onClick={() => handleSubmit()}
                data-testid="upload-document"
              >
                Upload
              </Button>
            </Box>
          </Panel>
        </Grid>
      )}
    </Grid>
  );
};

const UploadDocumentFormWrapped = withAutoFormNew<UploadDocumentFormType, formProps>(
  UploadDocumentForm,
  {}
);
